import React from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes} from 'react-router-dom';
import {App as AntdApp, ConfigProvider, theme} from 'antd';
import {antdLocaleMap} from './i18n/messages';
// 设计令牌（JS 侧）。AntD 的 darkAlgorithm 需要对 seed token 做颜色派生，
// 必须用真实色值，不能传 var(--pt-*)；与 styles/tokens.css 保持同步。
import {PT} from './styles/themeTokens';
import {useI18n} from './i18n/useI18n';
import Layout from './components/common/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import TradingDashboard from './pages/trading/TradingDashboard';
import SystemManagement from './pages/system/SystemManagement';
import TaskList from './pages/tasks/TaskList';
import DataFetchList from './pages/data-fetch/DataFetchList';
import ExecutionList from './pages/executions/ExecutionList';
import ExecutionDetail from './pages/executions/ExecutionDetail';
import AgentDebugPage from './pages/agent/AgentDebugPage';
import ProxyList from './pages/proxy/ProxyList';
import FreqtradeConfigList from './pages/proxy/FreqtradeConfigList';
import StrategyConfigList from './pages/proxy/StrategyConfigList';
import StrategyExecutionPage from './pages/freqtrade/StrategyExecutionPage';
import BacktestManagementPage from './pages/backtest/BacktestManagementPage';
import InstanceMonitorPage from './pages/freqtrade/InstanceMonitorPage';
import PositionList from './pages/positions';
import TradingPage from './pages/trading/TradingPage';
import HedgePage from './pages/hedge/HedgePage';
import BotPage from './pages/bot/BotPage';
import MembershipPage from './pages/membership/MembershipPage';
import MembershipGate from './pages/membership/MembershipGate';
import {MEMBERSHIP_REQUIRED} from './services/membershipService';
import AiModelConfig from './pages/ai-model-configs/AiModelConfig';
import AgentConfig from './pages/agent-configs/AgentConfig';
import AgentRoutingConfig from './pages/agent-routing-configs/AgentRoutingConfig';
import AgentOutputValidationPage from './pages/agent-validations/AgentOutputValidationPage';
import WorkflowBindings from './pages/workflow-bindings/WorkflowBindings';
import SkillConfig from './pages/skill-configs/SkillConfig';
import MemoryBlockManage from './pages/memory-blocks/MemoryBlockManage';
import MemoryPoolManage from './pages/memory-pool/MemoryPoolManage';
import PersonalInfo from './pages/system/PersonalInfo';
import UserManagement from './pages/system/UserManagement';
import RoleManagement from './pages/system/RoleManagement';
import PermissionManagement from './pages/system/PermissionManagement';
import InvitationCodeManagement from './pages/system/InvitationCodeManagement';
import ProxyRules from './pages/system/ProxyRules';
import AnnouncementManagement from './pages/system/AnnouncementManagement';
import VersionManagement from './pages/system/VersionManagement';
import ContactMessageManagement from './pages/system/ContactMessageManagement';
import WaitlistManagement from './pages/system/WaitlistManagement';
import NewsManagement from './pages/system/NewsManagement';
import SimulateSettings from './pages/system/SimulateSettings';
import ExchangeDataSettings from './pages/system/ExchangeDataSettings';
import OperationsSettings from './pages/system/OperationsSettings';
import PocDisplaySettings from './pages/system/PocDisplaySettings';
import SimulatePage from './pages/learning/SimulatePage';
import MyStrategyPage from './pages/strategy/MyStrategyPage';
import LogicFlowEditorPage from './pages/strategy/LogicFlowEditorPage';
import BacktestPage from './pages/backtest/BacktestPage';
import BacktestDetailPage from './pages/backtest/BacktestDetailPage';
import AiBacktestListPage from './pages/ai-backtest/AiBacktestListPage';
import AiBacktestDetailPage from './pages/ai-backtest/AiBacktestDetailPage';
import StrategyTasksPage from './pages/strategy-tasks/StrategyTasksPage';
import TokenStatsPage from './pages/token-stats/TokenStatsPage';
import ApiConnectionList from './pages/api-connection/ApiConnectionList';
import UserAiConfig from './pages/user-ai-config/UserAiConfig';
import UserAiPromptConfig from './pages/user-ai-config/UserAiPromptConfig';
import AssessmentPage from './pages/user/AssessmentPage';
import NewsPage from './pages/news/NewsPage';
import StrategySquarePage from './pages/tools/StrategySquarePage';
import RadarPage from './pages/tools/RadarPage';
import PatternRecognitionPage from './pages/tools/PatternRecognitionPage';
import CompositeSignalPage from './pages/tools/composite-signal/CompositeSignalPage';
import DcaAssistantPage from './pages/tools/dca-assistant/DcaAssistantPage';
import ResearchReportPage from './pages/tools/research-report/ResearchReportPage';
import PublicSiteLayout from './components/site/PublicSiteLayout';
import LocalizedPublicSiteLayout from './components/site/LocalizedPublicSiteLayout';
import HomePage from './pages/site/HomePage';
import ProductPage from './pages/site/ProductPage';
import ProductDirectionPage from './pages/site/ProductDirectionPage';
import SolutionsPage from './pages/site/SolutionsPage';
import ProgressPage from './pages/site/ProgressPage';
import ContactPage from './pages/site/ContactPage';
import SiteMembershipPage from './pages/site/MembershipPage';
import PocPage from './pages/site/PocPage';
import AssistTradingPocPage from './pages/site/AssistTradingPocPage';
import AutoTradingPocPage from './pages/site/AutoTradingPocPage';
import PatternPocPage from './pages/site/PatternPocPage';
import {MenuModeProvider} from './contexts/MenuModeContext';
import {SidebarProvider} from './contexts/SidebarContext';
import {AuthProvider} from './contexts/AuthContext';
import {setMessageApi} from './utils/messageHolder';

const DevKLineVisual = import.meta.env.DEV ? React.lazy(() => import('./pages/dev/KLineVisual')) : null;

const App: React.FC = () => {
    const {locale} = useI18n();

    return (
        <AntdApp>
            <style>{`
                .dark-modal-confirm .ant-modal-content {
                    background-color: var(--pt-bg-panel) !important;
                    border: 1px solid var(--pt-border) !important;
                }
                .dark-modal-confirm .ant-modal-header {
                    background-color: var(--pt-bg-panel) !important;
                    border-bottom: 1px solid var(--pt-border) !important;
                }
                .dark-modal-confirm .ant-modal-title {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-modal-body {
                    background-color: var(--pt-bg-panel) !important;
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-modal-confirm-body-wrapper {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-modal-confirm-title {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-modal-confirm-content {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-modal-confirm-btns .ant-btn {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-modal-confirm-btns .ant-btn-default {
                    background-color: var(--pt-bg-card) !important;
                    border-color: var(--pt-border) !important;
                }
                .dark-modal-confirm .ant-modal-footer {
                    background-color: var(--pt-bg-panel) !important;
                    border-top: 1px solid var(--pt-border) !important;
                }
                .dark-modal-confirm .ant-descriptions {
                    background-color: var(--pt-bg-panel) !important;
                }
                .dark-modal-confirm .ant-descriptions-view {
                    background-color: var(--pt-bg-panel) !important;
                }
                .dark-modal-confirm .ant-descriptions-item-label {
                    background-color: var(--pt-bg-panel) !important;
                    color: var(--pt-text-secondary) !important;
                    border-color: var(--pt-border) !important;
                }
                .dark-modal-confirm .ant-descriptions-item-content {
                    background-color: var(--pt-bg-panel) !important;
                    color: var(--pt-text-primary) !important;
                    border-color: var(--pt-border) !important;
                }
                .dark-modal-confirm .ant-descriptions-title {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-form-item-label > label {
                    color: var(--pt-text-secondary) !important;
                }
                .dark-modal-confirm .ant-radio-wrapper {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-input {
                    background-color: var(--pt-bg-card) !important;
                    border-color: var(--pt-border) !important;
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-input::placeholder {
                    color: var(--pt-text-secondary) !important;
                }
                .dark-modal-confirm .ant-tag {
                    background-color: rgba(255, 255, 255, 0.1) !important;
                    border-color: rgba(255, 255, 255, 0.13) !important;
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-typography {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-alert {
                    background-color: var(--pt-bg-base) !important;
                    border-color: var(--pt-border) !important;
                }
                .dark-modal-confirm .ant-alert-info {
                    background-color: var(--pt-bg-base) !important;
                    border-color: var(--pt-primary) !important;
                }
                .dark-modal-confirm .ant-alert-warning {
                    background-color: var(--pt-bg-base) !important;
                    border-color: var(--pt-amber) !important;
                }
                .dark-modal-confirm .ant-alert-error {
                    background-color: var(--pt-bg-base) !important;
                    border-color: var(--pt-bear) !important;
                }
                .dark-modal-confirm .ant-alert-message {
                    color: var(--pt-text-primary) !important;
                }
                .dark-modal-confirm .ant-alert-description {
                    color: var(--pt-text-primary) !important;
                }
                .ant-notification {
                    color: var(--pt-text-primary) !important;
                }
                .ant-notification-notice {
                    background-color: var(--pt-bg-panel) !important;
                    border-color: var(--pt-border) !important;
                }
                .ant-notification-notice-message {
                    color: var(--pt-text-primary) !important;
                    font-weight: 500 !important;
                }
                .ant-notification-notice-description {
                    color: var(--pt-text-secondary) !important;
                }
                .ant-notification-notice-close {
                    color: var(--pt-text-secondary) !important;
                }
                .ant-notification-notice-close:hover {
                    color: var(--pt-text-primary) !important;
                }
                /* Radio组件dark主题样式优化 */
                .ant-radio-wrapper {
                    color: var(--pt-text-primary) !important;
                    pointer-events: auto !important;
                    cursor: pointer !important;
                }
                .ant-radio-wrapper:hover {
                    color: var(--pt-text-primary) !important;
                }
                .ant-radio-wrapper-checked {
                    color: var(--pt-primary) !important;
                }
                .ant-radio {
                    pointer-events: auto !important;
                }
                .ant-radio-input {
                    pointer-events: auto !important;
                    cursor: pointer !important;
                }
                .ant-radio-inner {
                    background-color: var(--pt-bg-card) !important;
                    border-color: var(--pt-border) !important;
                    pointer-events: auto !important;
                }
                .ant-radio-wrapper:hover .ant-radio-inner {
                    border-color: var(--pt-primary) !important;
                }
                .ant-radio-wrapper-checked .ant-radio-inner {
                    background-color: var(--pt-primary) !important;
                    border-color: var(--pt-primary) !important;
                }
                .ant-radio-wrapper-checked .ant-radio-inner::after {
                    background-color: #ffffff !important;
                }
                .ant-radio-wrapper-checked::after {
                    border-color: var(--pt-primary) !important;
                }
                .ant-radio-label {
                    pointer-events: auto !important;
                    cursor: pointer !important;
                }
                /* Message组件样式强化 */
                .ant-message {
                    z-index: 9999 !important;
                    position: fixed !important;
                    top: 80px !important;
                    left: 50% !important;
                    transform: translateX(-50%) !important;
                    pointer-events: none !important;
                }
                .ant-message-notice {
                    background-color: var(--pt-bg-card) !important;
                    border: 1px solid var(--pt-border) !important;
                    border-radius: 6px !important;
                    box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
                               0 3px 6px -4px rgba(0, 0, 0, 0.12),
                               0 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
                    margin-bottom: 8px !important;
                    padding: 12px 16px !important;
                    pointer-events: auto !important;
                    min-width: 280px !important;
                    max-width: 500px !important;
                }
                .ant-message-notice-content {
                    color: var(--pt-text-primary) !important;
                    font-size: 14px !important;
                    line-height: 1.5 !important;
                }
                .ant-message-notice-success {
                    background-color: rgba(0, 200, 150, 0.12) !important;
                    border-color: var(--pt-bull) !important;
                }
                .ant-message-notice-error {
                    background-color: rgba(255, 107, 107, 0.12) !important;
                    border-color: var(--pt-bear) !important;
                }
                .ant-message-notice-warning {
                    background-color: rgba(250, 173, 20, 0.12) !important;
                    border-color: var(--pt-amber) !important;
                }
                .ant-message-notice-info {
                    background-color: rgba(240, 180, 41, 0.12) !important;
                    border-color: var(--pt-primary) !important;
                }
                .ant-message-notice-success .ant-message-notice-content {
                    color: var(--pt-bull) !important;
                }
                .ant-message-notice-error .ant-message-notice-content {
                    color: var(--pt-bear) !important;
                }
                .ant-message-notice-warning .ant-message-notice-content {
                    color: var(--pt-amber) !important;
                }
                .ant-message-notice-info .ant-message-notice-content {
                    color: var(--pt-primary) !important;
                }
            `}</style>
            <ConfigProvider
                locale={antdLocaleMap[locale]}
                theme={{
                    algorithm: theme.darkAlgorithm,
                    token: {
                        colorPrimary: PT.primary,
                        colorInfo: PT.primary,
                        colorSuccess: PT.bull,
                        colorWarning: PT.amber,
                        colorError: PT.bear,
                        colorBgBase: PT.bgBase,
                        colorBgContainer: PT.bgPanel,
                        colorBgElevated: PT.bgCard,
                        colorBgLayout: PT.bgBase,
                        colorText: PT.textPrimary,
                        colorTextSecondary: PT.textSecondary,
                        colorBorder: PT.border,
                        colorBorderSecondary: PT.borderSubtle,
                        controlItemBgActive: PT.primaryDim,
                        controlItemBgHover: PT.bgHover,
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        fontSize: PT.fontSizeBase,
                        // 尺寸：向 pattern 终端风格对齐 —— 紧凑控件 + 小圆角，提升信息密度
                        borderRadius: PT.radius,
                        borderRadiusSM: PT.radiusSM,
                        controlHeight: PT.controlHeight,
                        controlHeightSM: PT.controlHeightSM,
                        controlHeightLG: PT.controlHeightLG,
                    },
                    components: {
                        Modal: {
                            contentBg: PT.bgPanel,
                            headerBg: PT.bgPanel,
                            titleColor: PT.textPrimary,
                            colorIcon: PT.textPrimary,
                            colorIconHover: PT.primary,
                            footerBg: PT.bgPanel,
                            borderRadius: PT.radius,
                        },
                        Drawer: {
                            colorBgElevated: PT.bgPanel,
                            colorText: PT.textPrimary,
                            colorIcon: PT.textPrimary,
                            colorIconHover: PT.primary,
                            zIndexPopup: 1050
                        },
                        Menu: {
                            itemBg: 'transparent',
                            itemColor: PT.textPrimary,
                            itemSelectedColor: PT.primary,
                            itemHoverColor: PT.textPrimary,
                            groupTitleColor: PT.textSecondary,
                            itemSelectedBg: PT.primaryDim,
                            itemHoverBg: PT.bgHover,
                            itemBorderRadius: PT.radius,
                        },
                        Layout: {
                            siderBg: PT.bgPanel,
                            triggerBg: PT.bgPanel,
                        },
                        Notification: {
                            colorBgElevated: PT.bgPanel,
                            colorText: PT.textPrimary,
                            colorTextDescription: PT.textSecondary,
                            colorIcon: PT.textPrimary,
                            colorIconHover: PT.primary,
                            colorBorder: PT.border,
                            colorSuccess: PT.bull,
                            colorInfo: PT.primary,
                            colorWarning: PT.amber,
                            colorError: PT.bear,
                            fontSize: PT.fontSizeBase,
                            borderRadius: PT.radius,
                        },
                        Message: {
                            colorBgElevated: PT.bgCard,
                            colorText: PT.textPrimary,
                            colorSuccess: PT.bull,
                            colorInfo: PT.primary,
                            colorWarning: PT.amber,
                            colorError: PT.bear,
                            colorSuccessBg: PT.bullDim,
                            colorInfoBg: PT.primaryDim,
                            colorWarningBg: PT.amberDim,
                            colorErrorBg: PT.bearDim,
                            borderRadius: PT.radius,
                        },
                        Alert: {
                            colorBgElevated: PT.bgPanel,
                            colorText: PT.textPrimary,
                            colorTextHeading: PT.textPrimary,
                            colorTextDescription: PT.textSecondary,
                            colorBorder: PT.border,
                            colorSuccess: PT.bull,
                            colorInfo: PT.primary,
                            colorWarning: PT.amber,
                            colorError: PT.bear,
                            colorSuccessBorder: PT.bull,
                            colorInfoBorder: PT.primary,
                            colorWarningBorder: PT.amber,
                            colorErrorBorder: PT.bear,
                            colorSuccessBg: PT.bullDim,
                            colorInfoBg: PT.primaryDim,
                            colorWarningBg: PT.amberDim,
                            colorErrorBg: PT.bearDim,
                            borderRadius: PT.radius,
                        },
                        Button: {
                            colorBgContainer: PT.bgCard,
                            colorBgElevated: PT.bgHover,
                            colorText: PT.textPrimary,
                            colorPrimary: PT.primary,
                            colorPrimaryBg: PT.primaryDim,
                            colorPrimaryHover: PT.primaryHover,
                            colorPrimaryActive: PT.primaryActive,
                            colorTextLightSolid: PT.textPrimary,
                            borderRadius: PT.radius,
                        },
                        Descriptions: {
                            colorBgContainer: PT.bgPanel,
                            colorText: PT.textPrimary,
                            colorBorder: PT.border,
                            titleColor: PT.textPrimary,
                            labelColor: PT.textSecondary,
                        },
                        Form: {
                            colorBgContainer: PT.bgPanel,
                            colorText: PT.textPrimary,
                            colorBorder: PT.border,
                            colorHighlight: PT.primary,
                            colorError: PT.bear,
                            labelColor: PT.textSecondary,
                            itemMarginBottom: 24,
                        },
                        Radio: {
                            dotSize: 16,
                            radioSize: 16,
                        },
                        Tag: {
                            colorText: PT.textPrimary,
                            borderRadius: PT.radiusSM,
                        },
                        Input: {
                            colorBgContainer: PT.bgCard,
                            colorText: PT.textPrimary,
                            colorBorder: PT.border,
                            borderRadius: PT.radius,
                        },
                        Typography: {
                            colorText: PT.textPrimary,
                            colorTextSecondary: PT.textSecondary,
                            colorWarning: PT.amber,
                            colorError: PT.bear,
                            colorSuccess: PT.bull,
                            colorInfo: PT.primary,
                        },
                    },
                }}
            >
                <AuthProvider>
                    <SidebarProvider>
                        <MenuModeProvider>
                            <Router>
                                <Routes>
                                    {/* 官网公开路由 */}
                                    <Route path="/" element={<PublicSiteLayout><HomePage/></PublicSiteLayout>}/>
                                    <Route path="/site" element={<Navigate replace to="/"/>}/>
                                    <Route path="/site/product"
                                           element={<PublicSiteLayout><ProductPage/></PublicSiteLayout>}/>
                                    <Route path="/site/assist-trading" element={<PublicSiteLayout><ProductDirectionPage
                                        directionKey="assist-trading"/></PublicSiteLayout>}/>
                                    <Route path="/site/auto-trading" element={<PublicSiteLayout><ProductDirectionPage
                                        directionKey="auto-trading"/></PublicSiteLayout>}/>
                                    <Route path="/site/asset-allocation"
                                           element={<PublicSiteLayout><ProductDirectionPage
                                               directionKey="asset-allocation"/></PublicSiteLayout>}/>
                                    <Route path="/site/how-it-works"
                                           element={<PublicSiteLayout><SolutionsPage/></PublicSiteLayout>}/>
                                    <Route path="/site/risk-control"
                                           element={<PublicSiteLayout><ProgressPage/></PublicSiteLayout>}/>
                                    <Route path="/site/poc" element={<PublicSiteLayout><PocPage/></PublicSiteLayout>}/>
                                    <Route path="/site/poc/assist-trading"
                                           element={<PublicSiteLayout><AssistTradingPocPage/></PublicSiteLayout>}/>
                                    <Route path="/site/poc/auto-trading"
                                           element={<PublicSiteLayout><AutoTradingPocPage/></PublicSiteLayout>}/>
                                    <Route path="/site/poc/pattern"
                                           element={<PublicSiteLayout><PatternPocPage/></PublicSiteLayout>}/>
                                    <Route path="/site/contact"
                                           element={<PublicSiteLayout><ContactPage/></PublicSiteLayout>}/>
                                    <Route path="/site/membership"
                                           element={<PublicSiteLayout><SiteMembershipPage/></PublicSiteLayout>}/>
                                    {/* 多语言官网路由 */}
                                    <Route path="/:siteLocale" element={<LocalizedPublicSiteLayout/>}>
                                        <Route index element={<HomePage/>}/>
                                        <Route path="site/product" element={<ProductPage/>}/>
                                        <Route path="site/assist-trading"
                                               element={<ProductDirectionPage directionKey="assist-trading"/>}/>
                                        <Route path="site/auto-trading"
                                               element={<ProductDirectionPage directionKey="auto-trading"/>}/>
                                        <Route path="site/asset-allocation"
                                               element={<ProductDirectionPage directionKey="asset-allocation"/>}/>
                                        <Route path="site/how-it-works" element={<SolutionsPage/>}/>
                                        <Route path="site/risk-control" element={<ProgressPage/>}/>
                                        <Route path="site/poc" element={<PocPage/>}/>
                                        <Route path="site/poc/assist-trading" element={<AssistTradingPocPage/>}/>
                                        <Route path="site/poc/auto-trading" element={<AutoTradingPocPage/>}/>
                                        <Route path="site/poc/pattern" element={<PatternPocPage/>}/>
                                        <Route path="site/contact" element={<ContactPage/>}/>
                                        <Route path="site/membership" element={<SiteMembershipPage/>}/>
                                    </Route>
                                    {/* 公开路由 - 不需要登录 */}
                                    <Route path="/login" element={<LoginPage/>}/>
                                    <Route path="/register" element={<RegisterPage/>}/>
                                    <Route path="/assessment" element={<AssessmentPage/>}/>
                                    <Route path="/:siteLocale/login" element={<LoginPage/>}/>
                                    <Route path="/:siteLocale/register" element={<RegisterPage/>}/>
                                    <Route path="/:siteLocale/assessment" element={<AssessmentPage/>}/>

                                    {/* 受保护的路由 - 需要登录 */}
                                    <Route path="/dashboard" element={
                                        <ProtectedRoute
                                            requiredPermission="PAGE_TRADING_DASHBOARD"><Layout><TradingDashboard/></Layout></ProtectedRoute>}/>
                                    <Route path="/system" element={<ProtectedRoute><Layout><SystemManagement/></Layout></ProtectedRoute>}/>

                                    {/* 交易相关页面（对冲=专业 / 自动交易=专家，等级不足显示升级引导页） */}
                                    <Route path="/trading"
                                           element={<ProtectedRoute
                                               requiredPermission="PAGE_TRADING"><Layout><TradingPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/hedge"
                                           element={<ProtectedRoute
                                               requiredPermission="PAGE_TRADING"><Layout>
                                               <MembershipGate
                                                   requiredLevel={MEMBERSHIP_REQUIRED.HEDGE}><HedgePage/></MembershipGate>
                                           </Layout></ProtectedRoute>}/>
                                    <Route path="/positions"
                                           element={<ProtectedRoute
                                               requiredPermission="PAGE_POSITIONS"><Layout><PositionList/></Layout></ProtectedRoute>}/>
                                    <Route path="/bot"
                                           element={<ProtectedRoute
                                               requiredPermission="PAGE_BOT"><Layout>
                                               <MembershipGate
                                                   requiredLevel={MEMBERSHIP_REQUIRED.AUTO_TRADING}><BotPage/></MembershipGate>
                                           </Layout></ProtectedRoute>}/>

                                    {/* 策略中心 */}
                                    <Route path="/strategy/list" element={
                                        <ProtectedRoute
                                            requiredPermission="PAGE_STRATEGY_LIST"><Layout><MyStrategyPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/strategy/tasks" element={<ProtectedRoute
                                        requiredPermission="PAGE_STRATEGY_TASKS"><Layout><StrategyTasksPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/strategy/backtest"
                                           element={<ProtectedRoute
                                               requiredPermission="PAGE_BACKTEST"><Layout><BacktestPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/strategy/backtest/:id" element={
                                        <ProtectedRoute requiredPermission="PAGE_BACKTEST"><Layout><BacktestDetailPage/></Layout></ProtectedRoute>}/>

                                    {/* AI交易回测 */}
                                    <Route path="/ai-backtest"
                                           element={
                                               <ProtectedRoute
                                                   requiredPermission="PAGE_AI_BACKTEST"><Layout><AiBacktestListPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/ai-backtest/:id" element={
                                        <ProtectedRoute
                                            requiredPermission="PAGE_AI_BACKTEST"><Layout><AiBacktestDetailPage/></Layout></ProtectedRoute>}/>

                                    {/* Token统计 */}
                                    <Route path="/token-stats" element={
                                        <ProtectedRoute><Layout><TokenStatsPage/></Layout></ProtectedRoute>}/>

                                    {/* 学习中心 - 功能建设中 */}
                                    {/*<Route path="/learning/ai-strategy"*/}
                                    {/*       element={*/}
                                    {/*           <ProtectedRoute><Layout><AiStrategyPage/></Layout></ProtectedRoute>}/>*/}
                                    <Route path="/strategy/logic-flow"
                                           element={
                                               <ProtectedRoute
                                                   requiredPermission="PAGE_LOGIC_FLOW"><Layout><LogicFlowEditorPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/strategy/logic-flow/:id"
                                           element={
                                               <ProtectedRoute
                                                   requiredPermission="PAGE_LOGIC_FLOW"><Layout><LogicFlowEditorPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/learning/simulate"
                                           element={<ProtectedRoute
                                               requiredPermission="PAGE_SIMULATE"><Layout><SimulatePage/></Layout></ProtectedRoute>}/>

                                    {/* 工具 - 功能建设中 */}
                                    <Route path="/tools/radar"
                                           element={<ProtectedRoute requiredPermission="PAGE_RADAR"><Layout><RadarPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/tools/pattern"
                                           element={
                                               <ProtectedRoute
                                                   requiredPermission="PAGE_PATTERN"><Layout>
                                                   <MembershipGate
                                                       requiredLevel={MEMBERSHIP_REQUIRED.PATTERN}><PatternRecognitionPage/></MembershipGate>
                                               </Layout></ProtectedRoute>}/>
                                    <Route path="/tools/composite-signal"
                                           element={
                                               <ProtectedRoute
                                                   requiredPermission="PAGE_COMPOSITE_SIGNAL"><Layout><CompositeSignalPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/tools/dca-assistant"
                                           element={
                                               <ProtectedRoute
                                                   requiredPermission="PAGE_DCA_ASSISTANT"><Layout>
                                                   <MembershipGate
                                                       requiredLevel={MEMBERSHIP_REQUIRED.DCA_ASSISTANT}><DcaAssistantPage/></MembershipGate>
                                               </Layout></ProtectedRoute>}/>
                                    <Route path="/tools/research-report"
                                           element={
                                               <ProtectedRoute
                                                   requiredPermission="PAGE_RESEARCH_REPORT"><Layout><ResearchReportPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/tools/news"
                                           element={<ProtectedRoute
                                               requiredPermission="PAGE_NEWS"><Layout><NewsPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/tools/square" element={
                                        <ProtectedRoute
                                            requiredPermission="PAGE_STRATEGY_SQUARE"><Layout><StrategySquarePage/></Layout></ProtectedRoute>}/>

                                    {DevKLineVisual ? (
                                        <Route
                                            path="/__dev__/kline"
                                            element={
                                                <ProtectedRoute>
                                                    <React.Suspense fallback={null}>
                                                        <Layout><DevKLineVisual/></Layout>
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                    ) : null}

                                    {/* 用户配置/连接管理 */}
                                    <Route path="/user-ai-config"
                                           element={<ProtectedRoute
                                               requiredPermission="SYSTEM_AI_TRADING"><Layout><UserAiConfig/></Layout></ProtectedRoute>}/>
                                    <Route path="/user-ai-prompt-config"
                                           element={<ProtectedRoute
                                               requiredPermission="SYSTEM_AI_TRADING"><Layout><UserAiPromptConfig/></Layout></ProtectedRoute>}/>
                                    <Route path="/api-connection" element={<ProtectedRoute
                                        requiredPermission="PAGE_API_CONNECTION"><Layout><ApiConnectionList/></Layout></ProtectedRoute>}/>
                                    <Route path="/data-fetch" element={<ProtectedRoute
                                        requiredPermission="SYSTEM_DATA_FETCH_TASK"><Layout><DataFetchList/></Layout></ProtectedRoute>}/>
                                    <Route path="/proxy"
                                           element={<ProtectedRoute
                                               requiredPermission="SYSTEM_PROXY"><Layout><ProxyList/></Layout></ProtectedRoute>}/>
                                    <Route path="/tasks"
                                           element={<ProtectedRoute
                                               requiredPermission="SYSTEM_TASKS"><Layout><TaskList/></Layout></ProtectedRoute>}/>
                                    <Route path="/executions" element={<ProtectedRoute
                                        requiredPermission="SYSTEM_EXECUTIONS"><Layout><ExecutionList/></Layout></ProtectedRoute>}/>
                                    <Route path="/executions/:executionId" element={<ProtectedRoute
                                        requiredPermission="SYSTEM_EXECUTIONS"><Layout><ExecutionDetail/></Layout></ProtectedRoute>}/>
                                    <Route path="/agent/debug" element={<ProtectedRoute
                                        requiredPermission="PAGE_BOT"><Layout><AgentDebugPage/></Layout></ProtectedRoute>}/>

                                    {/* 系统管理页面 - 已在独立路径的删除 /system/ 冗余版本 */}
                                    <Route path="/system/ai-model-configs" element={
                                        <ProtectedRoute><Layout><AiModelConfig/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/agent-configs"
                                           element={<ProtectedRoute><Layout><AgentConfig/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/agent-output-validations"
                                           element={
                                               <ProtectedRoute><Layout><AgentOutputValidationPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/agent-routing-configs"
                                           element={
                                               <ProtectedRoute><Layout><AgentRoutingConfig/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/workflow-bindings"
                                           element={
                                               <ProtectedRoute><Layout><WorkflowBindings/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/skill-configs"
                                           element={<ProtectedRoute><Layout><SkillConfig/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/memory-blocks"
                                           element={
                                               <ProtectedRoute><Layout><MemoryBlockManage/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/memory-pool"
                                           element={
                                               <ProtectedRoute><Layout><MemoryPoolManage/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/freqtrade-config" element={
                                        <ProtectedRoute><Layout><FreqtradeConfigList/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/strategy-config" element={
                                        <ProtectedRoute><Layout><StrategyConfigList/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/strategy-execution" element={
                                        <ProtectedRoute><Layout><StrategyExecutionPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/backtest" element={
                                        <ProtectedRoute><Layout><BacktestManagementPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/strategy-execution/monitor/:instanceId" element={
                                        <ProtectedRoute><Layout><InstanceMonitorPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/personal-info"
                                           element={<ProtectedRoute><Layout><PersonalInfo/></Layout></ProtectedRoute>}/>
                                    <Route path="/membership"
                                           element={
                                               <ProtectedRoute><Layout><MembershipPage/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/user-management" element={
                                        <ProtectedRoute><Layout><UserManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/role-management" element={
                                        <ProtectedRoute><Layout><RoleManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/permission-management" element={
                                        <ProtectedRoute><Layout><PermissionManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/invitation-code-management" element={
                                        <ProtectedRoute><Layout><InvitationCodeManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/proxy-rules"
                                           element={<ProtectedRoute><Layout><ProxyRules/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/announcement-management" element={
                                        <ProtectedRoute><Layout><AnnouncementManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/version-management" element={
                                        <ProtectedRoute><Layout><VersionManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/news-management" element={
                                        <ProtectedRoute><Layout><NewsManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/simulate-settings" element={
                                        <ProtectedRoute><Layout><SimulateSettings/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/exchange-data-settings" element={
                                        <ProtectedRoute><Layout><ExchangeDataSettings/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/operations-settings" element={
                                        <ProtectedRoute
                                            requiredPermission="PAGE_OPERATIONS_CONFIG"><Layout><OperationsSettings/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/waitlist-management" element={
                                        <ProtectedRoute><Layout><WaitlistManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/contact-message-management" element={
                                        <ProtectedRoute><Layout><ContactMessageManagement/></Layout></ProtectedRoute>}/>
                                    <Route path="/system/poc-display-settings" element={
                                        <ProtectedRoute><Layout><PocDisplaySettings/></Layout></ProtectedRoute>}/>

                                    {/* 404处理 - 跳转到首页 */}
                                    <Route path="*" element={<Navigate to="/" replace/>}/>
                                </Routes>
                            </Router>
                        </MenuModeProvider>
                    </SidebarProvider>
                </AuthProvider>
            </ConfigProvider>
            <MessageHolderInit/>
        </AntdApp>
    );
};

/** 在 AntdApp 内初始化全局 message API，用于非组件代码（如 api.ts 拦截器） */
const MessageHolderInit: React.FC = () => {
    const {message: messageApi} = AntdApp.useApp();
    React.useEffect(() => {
        setMessageApi(messageApi);
    }, [messageApi]);
    return null;
};

export default App;
